import { Badge, Box, Card, Group, Stack, Text } from '@mantine/core';
import { FORMAT_RULES, type InteractionContent, type Outcome, type Question } from '@quizee/core';
import { questionTypeLabel } from '../format';
import { signupDraft } from '../interaction/draft';
import type { NewsletterContext } from '../interaction/newsletter';
import { SignupRead } from './SignupSection';

/**
 * An interaction as it stands, written to be read.
 *
 * The detail screen is opened to look at something far more often than to change it: to check what
 * a poll actually asks before pasting it into an article, to read what a model wrote before
 * approving it, to see which quiz the results underneath belong to. A screen that opens as a form
 * cannot answer any of those, because every field looks the same whether or not somebody meant to
 * touch it, and a stray keystroke on a live interaction is a change to what readers get.
 *
 * So this is the questions and answers as prose, in the order a reader meets them, with the things
 * that are true about them said in words: which answer is the right one, which outcome an answer
 * points at, how wide a scale runs. It is not a preview of the widget (the widget preview is that,
 * and it is a different job): it is the content, laid out to be checked.
 */
export function InteractionReadout({
  content,
  newsletter = null,
}: {
  content: InteractionContent;
  /** The brand's newsletter settings. Null on a screen that has not read them. */
  newsletter?: NewsletterContext | null;
}) {
  const rules = FORMAT_RULES[content.format];
  const single = rules.maxQuestions === 1;
  const outcomes = content.outcomes ?? [];

  return (
    <Stack className="interaction-readout" gap="lg">
      {content.introduction !== undefined && content.introduction !== '' && (
        <Box>
          <Text size="sm" c="dimmed">
            Introduction
          </Text>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{content.introduction}</Text>
        </Box>
      )}

      {outcomes.length > 0 && (
        <Box>
          <Text component="h3" fw={500} size="sm" mb="xs">
            Outcomes
          </Text>
          <Stack component="ul" gap="xs" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {outcomes.map((outcome) => (
              <Card component="li" key={outcome.id} withBorder radius="md" p="sm">
                <Text fw={500} size="sm">
                  {outcome.title}
                </Text>
                {outcome.description !== undefined && outcome.description !== '' && (
                  <Text size="sm" c="dimmed" mt={2}>
                    {outcome.description}
                  </Text>
                )}
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Text component="h3" fw={500} size="sm" mb="xs">
          {single ? 'The question' : 'Questions'}
        </Text>
        <Stack component="ol" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {content.questions.map((question, index) => (
            <Card
              className="question-readout"
              component="li"
              key={question.id}
              withBorder
              radius="md"
              p="lg"
            >
              <Text fw={500}>
                {single ? question.prompt : `${String(index + 1)}. ${question.prompt}`}
              </Text>
              <Text size="sm" c="dimmed" mt={2}>
                {questionTypeLabel(question.type)}
                {!question.required && ', and may be skipped'}
              </Text>
              <QuestionDetail question={question} outcomes={outcomes} />
            </Card>
          ))}
        </Stack>
      </Box>

      {content.completion_message !== undefined && content.completion_message !== '' && (
        <Box>
          <Text size="sm" c="dimmed">
            Finishing message
          </Text>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{content.completion_message}</Text>
        </Box>
      )}

      {newsletter !== null && (
        <SignupRead signup={signupDraft(content.signup)} newsletter={newsletter} />
      )}
    </Stack>
  );
}

/** What there is to say about one question beyond its wording: the answers, or the shape of one. */
function QuestionDetail({
  question,
  outcomes,
}: {
  question: Question;
  outcomes: readonly Outcome[];
}) {
  if (question.type === 'scale') {
    const scale = question.scale;
    if (scale === undefined) return null;
    return (
      <Text size="sm" mt="xs">
        From {scale.min}
        {scale.min_label === undefined || scale.min_label === ''
          ? ''
          : ` (${scale.min_label})`} to {scale.max}
        {scale.max_label === undefined || scale.max_label === '' ? '' : ` (${scale.max_label})`}.
      </Text>
    );
  }

  if (question.type === 'free_text') {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        {question.placeholder === undefined || question.placeholder === ''
          ? 'An empty box, with nothing suggested in it.'
          : `The empty box suggests: ${question.placeholder}`}
      </Text>
    );
  }

  return (
    <Stack
      className="answer-readout"
      component="ul"
      gap={4}
      mt="xs"
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {question.options.map((option) => (
        <Group component="li" key={option.id} gap="xs" align="center" wrap="wrap">
          <span
            className={`answer-indicator ${option.is_correct === true ? 'answer-correct' : ''}`}
            aria-hidden="true"
          >
            {option.is_correct === true && (
              <svg
                width="10"
                height="10"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="m3 8 3 3 7-7" />
              </svg>
            )}
          </span>
          <Text size="sm">{option.label}</Text>
          {option.is_correct === true && (
            <Badge size="sm" radius="sm" variant="light" color="sage">
              Correct
            </Badge>
          )}
          {pointsAt(option.outcome_weights, outcomes).map((title) => (
            <Text key={title} size="xs" c="dimmed">
              Points at {title}
            </Text>
          ))}
        </Group>
      ))}
    </Stack>
  );
}

/**
 * The outcomes an answer pushes towards, named rather than numbered.
 *
 * A weight is a number an editor set on a screen full of sliders, and it means nothing on its own
 * to somebody reading the quiz back. What they need to know is which result this answer counts
 * towards, so that is what this says.
 */
function pointsAt(
  weights: Readonly<Record<string, number>> | undefined,
  outcomes: readonly Outcome[],
): readonly string[] {
  if (weights === undefined) return [];
  return outcomes
    .filter((outcome) => (weights[outcome.id] ?? 0) > 0)
    .map((outcome) => outcome.title);
}
